import React, { useState, useEffect, useRef } from "react";
import toast, { Toaster } from "react-hot-toast";
import SingleDatePicker from "./SingleDatePicker";
import { rooms } from "../constants/rooms";
import { createRecurringTask, setPrePostOverride } from "../api/workOrder";

const RecurringTasks = ({ onClose, onSuccess, defaultDate }) => {
  const [taskForm, setTaskForm] = useState({
    title: "Pre-op Cleaning",
    rooms: [],
    roomConfigs: {},
    changeoverConfig: {
      sameMachine: 5,
      differentMachine: 10,
    },
    startTime: "08:00",
    duration: 15,
    cadence: "daily",
    startDate: defaultDate || new Date(),
    endDate: null,
    applyToAllRooms: false,
  });

  const selectableRooms =
    taskForm?.title === "Changeover"
      ? rooms.filter((room) => !["Bulk Zone", "Manual Zone"].includes(room))
      : rooms;

  const [isRoomDropdownOpen, setIsRoomDropdownOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dropdownRef = useRef(null);
  const [overrideDaily, setOverrideDaily] = useState(false);

  const cadences =
    taskForm.title === "Changeover" || taskForm.title === "Meeting"
      ? ["daily"]
      : ["daily", "selected-date"];
  const cadenceLabels = {
    daily: "Daily",
    "selected-date": "Selected Date Only",
  };
  const taskTypes = ["Meeting", "Pre-op Cleaning", "Changeover", "Post-op Cleaning"];

  const preOpCleaningConfig = {
    ROOM1: { startTime: "08:00", duration: 15 },
    ROOM2: { startTime: "08:00", duration: 30 },
    ROOM3: { startTime: "08:00", duration: 20 },
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsRoomDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleInputChange = (key, value) => {
    if (key.startsWith("changeover.")) {
      const field = key.split(".")[1];
      setTaskForm((prev) => ({
        ...prev,
        changeoverConfig: {
          ...prev.changeoverConfig,
          [field]: parseInt(value) || 0,
        },
      }));
    } else if (key === "title") {
      setTaskForm((prev) => ({
        ...prev,
        title: value,
        cadence: (value === "Changeover" || value === "Meeting") ? "daily" : prev.cadence,
        roomConfigs: value === "Pre-op Cleaning" ? prev.roomConfigs : {},
        changeoverConfig:
          value === "Changeover"
            ? prev.changeoverConfig
            : { sameMachine: 5, differentMachine: 10 },
        duration: value === "Meeting" ? 15 : prev.duration,
        startTime: value === "Meeting" ? "08:00" : prev.startTime,
      }));
      setOverrideDaily(false);
    } else if (key === "rooms") {
      const newRooms = value;
      let newRoomConfigs = { ...taskForm.roomConfigs };

      if (taskForm.title === "Pre-op Cleaning") {
        newRooms.forEach((room) => {
          const configKey = room.toUpperCase().replace(/\s/g, "");
          if (preOpCleaningConfig[configKey] && !newRoomConfigs[room]) {
            newRoomConfigs[room] = preOpCleaningConfig[configKey];
          }
        });
        Object.keys(newRoomConfigs).forEach((room) => {
          if (!newRooms.includes(room)) delete newRoomConfigs[room];
        });
      } else {
        newRoomConfigs = {};
      }

      setTaskForm((prev) => ({
        ...prev,
        rooms: newRooms,
        roomConfigs: newRoomConfigs,
      }));
    } else {
      setTaskForm((prev) => ({ ...prev, [key]: value }));
    }
  };

  const handleRoomChange = (room) => {
    if (room === "Apply to All") {
      const willApplyToAll = !taskForm.applyToAllRooms;
      setTaskForm((prev) => ({
        ...prev,
        applyToAllRooms: willApplyToAll,
        // rooms: willApplyToAll ? rooms : [],
        rooms: willApplyToAll ? selectableRooms : [],
        roomConfigs: willApplyToAll
          ? prev.title === "Pre-op Cleaning"
            ? // ? rooms.reduce((configs, r) => {
              selectableRooms.reduce((configs, r) => {
                const key = r.toUpperCase().replace(/\s/g, "");
                configs[r] = preOpCleaningConfig[key] || {
                  startTime: "08:00",
                  duration: 15,
                };
                return configs;
              }, {})
            : {}
          : {},
      }));
    } else {
      if (taskForm.applyToAllRooms) {
        setTaskForm((prev) => ({
          ...prev,
          applyToAllRooms: false,
          rooms: [room],
          roomConfigs:
            prev.title === "Pre-op Cleaning"
              ? {
                  [room]: preOpCleaningConfig[
                    room.toUpperCase().replace(/\s/g, "")
                  ] || { startTime: "08:00", duration: 15 },
                }
              : {},
        }));
      } else {
        const newRooms = taskForm.rooms.includes(room)
          ? taskForm.rooms.filter((r) => r !== room)
          : [...taskForm.rooms, room];

        let configs = { ...taskForm.roomConfigs };
        if (taskForm.title === "Pre-op Cleaning") {
          newRooms.forEach((r) => {
            const key = r.toUpperCase().replace(/\s/g, "");
            if (preOpCleaningConfig[key] && !configs[r]) {
              configs[r] = preOpCleaningConfig[key];
            }
          });
          Object.keys(configs).forEach(
            (r) => !newRooms.includes(r) && delete configs[r]
          );
        } else {
          configs = {};
        }

        setTaskForm((prev) => ({
          ...prev,
          rooms: newRooms,
          roomConfigs: configs,
        }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (!taskForm.title.trim()) return toast.error("Task Title is required.", {position:"top-right"});

    if (taskForm.title === "Changeover") {
      if (
        taskForm.changeoverConfig.sameMachine < 1 ||
        taskForm.changeoverConfig.differentMachine < 1
      ) {
        return toast.error("Changeover durations must be at least 1 minute.", {position:"top-right"});
      }
    } else {
      const durationValue = Number(taskForm.duration);
      if (
        taskForm.duration === "" ||
        taskForm.duration === null ||
        taskForm.duration === undefined ||
        Number.isNaN(durationValue) ||
        durationValue < 0
      ) {
        return toast.error("Duration must be a number >= 0.", {
          position: "top-right",
        });
      }
    }

    if (taskForm.endDate && taskForm.endDate < taskForm.startDate)
      return toast.error("End Date cannot be before Start Date.", {position:"top-right"});

    if (!taskForm.applyToAllRooms && taskForm.rooms.length === 0)
      return toast.error("Select at least one room.", {position:"top-right"});

    setIsSubmitting(true);

    try {
      // const selectedRooms = taskForm.applyToAllRooms ? rooms : taskForm.rooms;
      const selectedRooms = taskForm.applyToAllRooms
        ? selectableRooms
        : taskForm.rooms;

      const isSelectedDateOnly = taskForm.cadence === "selected-date";
      const dateKey = taskForm.startDate.toISOString().split("T")[0];

      if (isSelectedDateOnly && taskForm.title !== "Changeover") {
        const overridePayload = {
          rooms: selectedRooms,
          date: dateKey,
          ...(taskForm.title === "Pre-op Cleaning"
            ? {
                preStartTime: taskForm.startTime,
                preDuration: taskForm.duration,
              }
            : {
                postStartTime: taskForm.startTime,
                postDuration: taskForm.duration,
              }),
        };

        await setPrePostOverride(overridePayload);

        toast.success("Pre/Post override saved for selected date.", {
          position: "top-right",
        });
      } else {
        const payload = {
          title: taskForm.title,
          room: selectedRooms,
          cadence: taskForm.cadence,
          startDate: overrideDaily ? new Date().toISOString().split("T")[0] : dateKey,
          endDate: taskForm.endDate?.toISOString().split("T")[0] || null,
          applyToAllRooms: taskForm.applyToAllRooms,
          overrideExisting: overrideDaily,

          // Conditional payload based on task type
          ...(taskForm.title === "Changeover"
            ? {
                changeoverDurationSameMachine:
                  taskForm.changeoverConfig.sameMachine,
                changeoverDurationDifferentMachine:
                  taskForm.changeoverConfig.differentMachine,
              }
            : {
                duration: taskForm.duration,
                startTime: taskForm.startTime,
              }),
        };

        await createRecurringTask(payload);

        toast.success("Recurring task(s) added successfully!", {
          position: "top-right",
        });
      }
      onClose();
      onSuccess?.();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to create task.", {position:"top-right"});
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="relative">
      <Toaster />
      <form
        onSubmit={handleSubmit}
        className="grid grid-cols-1 sm:grid-cols-2 gap-4"
      >
        {/* Task Title */}
        <div>
          <label className="block text-sm font-medium text-gray-900 mb-1">
            Task Title
          </label>
          <select
            value={taskForm.title}
            onChange={(e) => handleInputChange("title", e.target.value)}
            className="w-full py-2 px-3 rounded border border-gray-300 bg-white text-sm text-gray-900 focus:ring-2 focus:ring-gray-500 focus:outline-none"
            required
            disabled={isSubmitting}
          >
            {taskTypes.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>

        {/* Rooms */}
        <div ref={dropdownRef}>
          <label className="block text-sm font-medium text-gray-900 mb-1">
            Rooms
          </label>
          <div className="relative">
            <button
              type="button"
              onClick={() => setIsRoomDropdownOpen(!isRoomDropdownOpen)}
              disabled={isSubmitting}
              className="w-full h-10 px-3 rounded border border-gray-300 bg-white text-sm text-gray-900 text-left flex items-center gap-2 focus:ring-2 focus:ring-gray-500 focus:outline-none disabled:opacity-50 overflow-hidden"
            >
              <div className="flex items-center gap-1.5 flex-wrap overflow-hidden">
                {taskForm.applyToAllRooms ? (
                  <span className="bg-black text-white px-2 py-0.5 rounded text-xs font-medium">
                    All Rooms
                  </span>
                ) : taskForm.rooms.length === 0 ? (
                  <span className="text-gray-500">Select Rooms</span>
                ) : (
                  <>
                    {taskForm.rooms.slice(0, 3).map((room) => (
                      <span
                        key={room}
                        className="bg-gray-200 text-gray-800 px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap"
                      >
                        {room}
                      </span>
                    ))}
                    {taskForm.rooms.length > 3 && (
                      <span className="text-xs text-gray-600 font-medium">
                        +{taskForm.rooms.length - 3} more
                      </span>
                    )}
                  </>
                )}
              </div>

              {/* Dropdown arrow */}
              <svg
                className={`w-4 h-4 flex-shrink-0 transition-transform duration-200 ml-auto ${
                  isRoomDropdownOpen ? "rotate-180" : ""
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
            {isRoomDropdownOpen && !isSubmitting && (
              <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg max-h-40 overflow-y-auto">
                <div
                  className="flex items-center px-3 py-2 hover:bg-gray-100"
                  onClick={(e) => e.stopPropagation()}
                >
                  <input
                    type="checkbox"
                    id="apply-all"
                    checked={taskForm.applyToAllRooms}
                    onChange={() => handleRoomChange("Apply to All")}
                    className="h-4 w-4 text-gray-900 border-gray-300 rounded focus:ring-gray-500"
                    onClick={(e) => e.stopPropagation()}
                  />
                  <label
                    htmlFor="apply-all"
                    className="ml-2 text-sm text-gray-900"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Apply to All
                  </label>
                </div>
                {(taskForm.title === "Changeover"
                  ? rooms.filter(
                      (r) => !["Bulk Zone", "Manual Zone"].includes(r)
                    )
                  : rooms
                ).map((room) => (
                  <div
                    key={room}
                    className="flex items-center px-3 py-2 hover:bg-gray-100"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      id={`room-${room}`}
                      checked={taskForm.rooms.includes(room)}
                      onChange={() => handleRoomChange(room)}
                      className="h-4 w-4 text-gray-900 border-gray-300 rounded focus:ring-gray-500"
                      disabled={taskForm.applyToAllRooms}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <label
                      htmlFor={`room-${room}`}
                      className="ml-2 text-sm text-gray-900"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {room}
                    </label>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Start Time - Only for non-Changeover */}
        {taskForm.title !== "Changeover" && (
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-1">
              Start Time
            </label>
            <input
              type="time"
              value={taskForm.startTime}
              onChange={(e) => handleInputChange("startTime", e.target.value)}
              className="w-full py-2 px-3 rounded border border-gray-300 bg-white text-sm text-gray-900 focus:ring-2 focus:ring-gray-500 focus:outline-none"
              required
              disabled={isSubmitting}
            />
          </div>
        )}

        {/* Duration - Conditional */}
        {taskForm.title === "Changeover" ? (
          <div className="sm:col-span-2 space-y-4">
            <label className="block text-sm font-medium text-gray-900">
              Changeover Duration Settings
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Same Machine Changeover (mins)
                </label>
                <input
                  type="number"
                  value={taskForm.changeoverConfig.sameMachine}
                  onChange={(e) =>
                    handleInputChange("changeover.sameMachine", e.target.value)
                  }
                  className="w-full py-2 px-3 rounded border border-gray-300 text-sm"
                  min="1"
                  required
                  disabled={isSubmitting}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Different Machine Changeover (mins)
                </label>
                <input
                  type="number"
                  value={taskForm.changeoverConfig.differentMachine}
                  onChange={(e) =>
                    handleInputChange(
                      "changeover.differentMachine",
                      e.target.value
                    )
                  }
                  className="w-full py-2 px-3 rounded border border-gray-300 text-sm"
                  min="1"
                  required
                  disabled={isSubmitting}
                />
              </div>
            </div>
            <p className="text-xs text-gray-500">
              The system will automatically use the correct duration based on
              whether the previous workorder used the same machine.
            </p>
          </div>
        ) : (
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-1">
              Duration (min)
            </label>
            <input
              type="number"
              value={taskForm.duration}
              onChange={(e) =>
                handleInputChange(
                  "duration",
                  e.target.value === "" ? "" : Number(e.target.value),
                )
              }
              className="w-full py-2 px-3 rounded border border-gray-300 bg-white text-sm text-gray-900 focus:ring-2 focus:ring-gray-500 focus:outline-none"
              min="0"
              required
              disabled={isSubmitting}
            />
          </div>
        )}

        {/* Cadence */}
        <div>
          <label className="block text-sm font-medium text-gray-900 mb-1">
            Cadence
          </label>
          <select
            value={taskForm.cadence}
            onChange={(e) => handleInputChange("cadence", e.target.value)}
            className="w-full py-2 px-3 rounded border border-gray-300 bg-white text-sm text-gray-900 focus:ring-2 focus:ring-gray-500 focus:outline-none"
            disabled={isSubmitting}
          >
            {cadences.map((c) => (
              <option key={c} value={c}>
                {cadenceLabels[c] || c}
              </option>
            ))}
          </select>
          {taskForm.title !== "Changeover" && taskForm.cadence === "daily" && (
            <p className="text-xs text-gray-600 mt-2">
              If you want to change for a particular day only, please select
              "Selected Date Only".
            </p>
          )}
        </div>

        {/* Start Date */}
        <div>
          <label className="block text-sm font-medium text-gray-900 mb-1">
            Start Date
          </label>
          <SingleDatePicker
            value={taskForm.startDate}
            onChange={(d) => handleInputChange("startDate", d)}
            minDate={new Date()}
            className="w-full py-2 px-3 rounded border border-gray-300 bg-white text-sm text-gray-900 focus:ring-2 focus:ring-gray-500 focus:outline-none"
            disabled={isSubmitting}
          />
        </div>

        {/* Override Daily */}
        {taskForm.title !== "Changeover" && taskForm.cadence === "daily" && (
          <div className="sm:col-span-2">
            <div className="flex items-center gap-3 rounded border border-gray-200 p-3 bg-gray-50">
              <input
                id="override-daily"
                type="checkbox"
                checked={overrideDaily}
                onChange={(e) => setOverrideDaily(e.target.checked)}
                disabled={isSubmitting}
                className="h-4 w-4 text-gray-900 border-gray-300 rounded focus:ring-gray-500"
              />
              <label
                htmlFor="override-daily"
                className="text-sm text-gray-900"
              >
                Override existing daily schedule (from today forward)
              </label>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Past dates keep the old pre/post values.
            </p>
          </div>
        )}

        {/* Buttons */}
        <div className="flex items-end sm:col-span-2 gap-4 mt-4">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="flex-1 py-2 px-4 rounded bg-gray-300 hover:bg-gray-400 text-gray-900 font-semibold text-sm focus:ring-2 focus:ring-gray-500 focus:outline-none disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="flex-1 py-2 px-4 rounded text-white bg-black hover:bg-gray-800 font-semibold text-sm focus:ring-2 focus:ring-gray-500 focus:outline-none disabled:bg-gray-500"
          >
            {isSubmitting ? "Adding..." : "Add Recurring Task"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default RecurringTasks;
